import { Measurement } from "./schemas/measurement.js";
import { buildCursorPath, type FetchPage } from "./asyncList.js";

// The ONE definition of "fetch a page of measurements" (PG-37, CN-48 slice).
//
// It used to exist twice: apps/mobile/src/measurements/useMeasurements.ts
// walked the cursor through useAsyncList, apps/web-rnw/src/useMeasurementsWeb.ts
// drained every page in a hand-rolled do/while. Same screen, same endpoint,
// two behaviours — so the tail past the server's default page was silently
// unreachable on one surface and eagerly (and unboundedly) drained on the
// other. The two copies differed ONLY in which apiFetch they closed over,
// which is exactly the shape §CONSOLIDAMENTO says to collapse rather than
// duplicate once more.

/** Rows per request. Deliberately the server's own default rather than
 *  something smaller: this list also feeds the trend CHARTS on the
 *  Measurements screen, so a shorter first page would quietly shorten
 *  every curve until the athlete scrolled. Server clamp: max 365
 *  (`clampLimit`, apps/api/src/lib/pagination.ts). */
export const MEASUREMENTS_PAGE_SIZE = 100;

export const MEASUREMENTS_PATH = "/api/me/measurements";

/** What the endpoint answers. Rows stay `unknown` on purpose: they are
 *  server JSON until the schema has had a look at them. */
export type MeasurementsListResponse = { items: unknown[]; cursor: string | null };

/** The per-platform seam: mobile and web each bring their own audited
 *  apiFetch (auth headers, retry, deadlines) — this module never builds a
 *  request itself, it only says WHICH one and how to read the answer.
 *  ⛔ Deliberately NOT generic (`<T>(path) => Promise<T>`): a generic
 *  function type cannot be satisfied by a test double, so the seam would
 *  have been unmockable — and a seam nobody can stand in for is not a seam. */
export type PageFetcher = (path: string) => Promise<MeasurementsListResponse>;

export function measurementsPagePath(cursor: string | null): string {
  return buildCursorPath(MEASUREMENTS_PATH, `limit=${MEASUREMENTS_PAGE_SIZE}`, cursor);
}

/** Rows that fail the schema are DROPPED, not thrown on: one malformed
 *  historical row must not blank a whole page of an athlete's history. */
export function makeMeasurementsFetchPage(
  fetchJson: PageFetcher,
): FetchPage<Measurement, string> {
  return async (cursor) => {
    const res = await fetchJson(measurementsPagePath(cursor));
    const items: Measurement[] = [];
    for (const raw of res.items ?? []) {
      const parsed = Measurement.safeParse(raw);
      if (parsed.success) items.push(parsed.data);
    }
    return { items, cursor: res.cursor };
  };
}
